/**
 * Credential Service
 * Manages iTop API credentials using Office Roaming Settings
 */

import { ITopCredentials } from '../models/itop.models';

export class CredentialService {
  private static readonly KEYS = {
    username: 'itop_username',
    password: 'itop_password',
    url: 'itop_url'
  };

  // Default iTop URL for Solvia
  static readonly DEFAULT_URL = 'https://ticketing.solvia.ch/webservices/rest.php?version=1.3';

  /**
   * Check if Office Roaming Settings are available
   */
  static isAvailable(): boolean {
    return !!(
      typeof Office !== 'undefined' &&
      Office.context &&
      Office.context.roamingSettings
    );
  }

  /**
   * Save credentials to Office Roaming Settings
   */
  static save(username: string, password: string, url?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isAvailable()) {
        reject(new Error('Office Roaming Settings not available'));
        return;
      }

      const settings = Office.context.roamingSettings;
      settings.set(this.KEYS.username, username);
      settings.set(this.KEYS.password, btoa(password)); // Base64 encode for obfuscation
      settings.set(this.KEYS.url, url || this.DEFAULT_URL);

      settings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || 'Failed to save settings'));
        }
      });
    });
  }

  /**
   * Get stored credentials
   * Returns null if credentials not configured or roaming settings unavailable
   */
  static get(): ITopCredentials | null {
    if (!this.isAvailable()) {
      console.warn('Office Roaming Settings not available');
      return null;
    }

    const settings = Office.context.roamingSettings;
    const username = settings.get(this.KEYS.username) as string | undefined;
    const password = settings.get(this.KEYS.password) as string | undefined;
    const url = settings.get(this.KEYS.url) as string | undefined;

    if (!username || !password) {
      return null;
    }

    return {
      username,
      password: atob(password),
      url: url || this.DEFAULT_URL
    };
  }

  /**
   * Clear stored credentials
   */
  static clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isAvailable()) {
        reject(new Error('Office Roaming Settings not available'));
        return;
      }

      const settings = Office.context.roamingSettings;
      settings.remove(this.KEYS.username);
      settings.remove(this.KEYS.password);
      settings.remove(this.KEYS.url);

      settings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || 'Failed to clear settings'));
        }
      });
    });
  }

  /**
   * Check if credentials are configured
   */
  static hasCredentials(): boolean {
    return this.get() !== null;
  }

  /**
   * Get the iTop URL (for display purposes)
   */
  static getUrl(): string {
    const creds = this.get();
    return creds?.url || this.DEFAULT_URL;
  }
}
